import { create } from 'zustand';
import type { User } from '@blackboard/shared';

interface AuthState {
  token: string | null;
  user: User | null;
  setAuth: (token: string | null, user: User | null) => void;
  logout: () => void;
}

/** Decode JWT exp without signature verification — UI hint only; backend re-verifies every request. */
function readExp(jwt: string): number | null {
  try {
    const payload = jwt.split('.')[1];
    if (!payload) return null;
    // base64url → base64: pad to multiple of 4 so atob doesn't silently fail
    // on tokens whose payload length isn't divisible by 4 (Tech R2 P1).
    let b64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    while (b64.length % 4) b64 += '=';
    const json = atob(b64);
    const claims = JSON.parse(json) as { exp?: number };
    return typeof claims.exp === 'number' ? claims.exp : null;
  } catch {
    return null;
  }
}

function loadInitialToken(): string | null {
  if (typeof localStorage === 'undefined') return null;
  const t = localStorage.getItem('bb_token');
  if (!t) return null;
  const exp = readExp(t);
  if (exp !== null && exp * 1000 < Date.now()) {
    localStorage.removeItem('bb_token');
    return null;
  }
  return t;
}

export const useAuth = create<AuthState>((set) => ({
  token: loadInitialToken(),
  user: null,
  setAuth: (token, user) => {
    if (typeof localStorage !== 'undefined') {
      if (token) localStorage.setItem('bb_token', token);
      else localStorage.removeItem('bb_token');
    }
    set({ token, user });
  },
  logout: () => {
    if (typeof localStorage !== 'undefined') localStorage.removeItem('bb_token');
    set({ token: null, user: null });
  },
}));
